import React from 'react';
import { Card, Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import CelebrationIcon from '@mui/icons-material/Celebration';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import TemplateFooterVariants from './TemplateFooterVariants';

const StyledCard = styled(Card)(({ theme }) => ({
  cursor: 'pointer',
  borderRadius: '10px',
  border: '1px solid #e2e8f0',
  transition: 'all 0.25s ease',
  overflow: 'hidden',
  padding: 0,
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  WebkitTapHighlightColor: 'transparent',
  '&:active': {
    transform: 'scale(0.98)',
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)'
  },
  [theme.breakpoints.up('sm')]: {
    borderRadius: '12px',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.06)'
  },
  [theme.breakpoints.up('md')]: {
    borderRadius: '14px',
    boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: '0 12px 32px rgba(0, 0, 0, 0.12)',
      borderColor: '#0B91D6',
      '& .design-placeholder': {
        background: 'linear-gradient(135deg, #0B91D6 0%, #0A7BB8 100%)'
      },
      '& .design-icon': {
        transform: 'scale(1.1)'
      }
    },
    '&:active': {
      transform: 'translateY(-2px)'
    }
  }
}));

const DesignPlaceholder = styled(Box)(({ theme }) => ({
  height: '160px',
  minHeight: '160px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '12px',
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  transition: 'background 0.3s ease',
  flexShrink: 0,
  [theme.breakpoints.up('sm')]: {
    height: '180px',
    minHeight: '180px',
    gap: '14px'
  },
  [theme.breakpoints.up('md')]: {
    height: '200px',
    minHeight: '200px',
    gap: '16px'
  }
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  width: '48px',
  height: '48px',
  borderRadius: '50%',
  backgroundColor: 'rgba(255, 255, 255, 0.2)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'transform 0.3s ease',
  '& svg': {
    fontSize: '24px',
    color: '#ffffff'
  },
  [theme.breakpoints.up('sm')]: {
    width: '56px',
    height: '56px',
    '& svg': {
      fontSize: '28px'
    }
  },
  [theme.breakpoints.up('md')]: {
    width: '64px',
    height: '64px',
    '& svg': {
      fontSize: '32px'
    }
  }
}));

const getDesignIcon = (designId, title) => {
  const lowerTitle = (title || '').toLowerCase();
  if (lowerTitle.includes('festival') || designId === 1) {
    return <CelebrationIcon />;
  }
  if (lowerTitle.includes('special') || designId === 5) {
    return <CalendarTodayIcon />;
  }
  if (lowerTitle.includes('offer') || lowerTitle.includes('product') || designId === 6) {
    return <LocalOfferIcon />;
  }
  return <CelebrationIcon />;
};

const getPlaceholderGradient = (designId) => {
  switch (designId) {
    case 1: // Festival
      return 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)';
    case 5: // Special Day
      return 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)';
    case 6: // Offers & Products
      return 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)';
    default:
      return 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
  }
};

const FooterWrapper = styled('div')(({ theme }) => ({
  height: '110px',
  minHeight: '110px',
  maxHeight: '110px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-end',
  overflow: 'hidden',
  '& > *': {
    height: '100%',
    minHeight: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center'
  },
  [theme.breakpoints.up('sm')]: {
    height: '125px',
    minHeight: '125px',
    maxHeight: '125px'
  },
  [theme.breakpoints.up('md')]: {
    height: '140px',
    minHeight: '140px',
    maxHeight: '140px'
  }
}));

const DesignCard = ({ design, onClick, accountInfo }) => {
  return (
    <StyledCard onClick={onClick}>
      <DesignPlaceholder
        className="design-placeholder"
        sx={{ background: getPlaceholderGradient(design.id) }}
      >
        <IconWrapper className="design-icon">
          {getDesignIcon(design.id, design.title)}
        </IconWrapper>
      </DesignPlaceholder>
      <FooterWrapper>
        <TemplateFooterVariants designId={design.id} accountInfo={accountInfo} />
      </FooterWrapper>
    </StyledCard>
  );
};

export default DesignCard;
